// Browser-side helpers that hydrate AgentConnection rows with the
// human-friendly card metadata (name/description/iconUrl) published in the
// agent manifest. Lives in `lib/vault/` so the vault page (and any future
// page that lists agents) can share the fetch path.

import type { AgentCard } from '@/src/data/agent-cards';
import {
  fetchMarketplaceAgent,
  MarketplaceFetchError,
  type MarketplaceFetchReason,
} from '@/src/marketplace/client';
import type { MarketplaceAgentManifest } from '@/src/marketplace/types';
import { displayAgentName } from './agent-display';

/** Subset of {@link MarketplaceAgentManifest} the dashboard card needs. */
export type ManifestResponse = MarketplaceAgentManifest;

/** Reason a manifest fetch failed — re-exported under the local name so
 *  callers stay decoupled from the marketplace-client implementation. */
export type FetchManifestErrorReason = MarketplaceFetchReason;

export interface FetchManifestOptions {
  /**
   * Invoked when the fetch fails. `detail` is `Error` for network/parse
   * and `{ status: number }` for non-2xx. The function still returns
   * null afterward so the caller's fallback path runs unchanged.
   *
   * AGENTS.md § Logging — we don't pull `console` into a browser-side
   * lib. Callers that want a log line, a toast, or telemetry attach it
   * here.
   */
  onError?: (reason: FetchManifestErrorReason, detail: unknown) => void;
}

/**
 * Fetch the marketplace manifest for an agent. Delegates to the central
 * marketplace client so dashboard card hydration and the AgentDetailClient
 * share a single dedup'd request and response cache. Returns null on every
 * failure path (so the UI's empty-card fallback keeps working) and routes
 * the failure into `opts.onError` when supplied.
 */
export async function fetchManifest(
  marketplaceBaseUrl: string,
  agentId: string,
  opts: FetchManifestOptions = {},
): Promise<ManifestResponse | null> {
  try {
    return await fetchMarketplaceAgent(marketplaceBaseUrl, agentId);
  } catch (err) {
    if (err instanceof MarketplaceFetchError) {
      opts.onError?.(err.reason, err.detail);
    } else {
      // Anything that escapes the typed-error path is treated as a
      // network failure — the alternative is to let the error bubble,
      // which the silent-default contract forbids.
      opts.onError?.('network', err);
    }
    return null;
  }
}

// The manifest no longer carries a `card`. The vault dashboard derives a
// display card from the agentId and the first engagement's goal so the row
// reads as a sentence rather than an opaque slug.
export function cardFromManifest(m: Partial<ManifestResponse>, fallbackId: string): AgentCard {
  const cardName = m.card?.name?.trim();
  const cardDescription = m.card?.description?.trim();
  const goal = m.engagements?.[0]?.goal?.trim();
  return {
    name: cardName || displayAgentName(fallbackId),
    description: cardDescription || goal || undefined,
    iconUrl: m.card?.iconUrl || undefined,
    ...(m.widgets !== undefined ? { widgets: m.widgets } : {}),
  };
}
